import { Alert, Button, Flex, Typography } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SlaModalShell } from '~/app/landing/sla/shared/components/SlaModalShell';
import { resolveSlaModalLayout } from '~/app/landing/sla/shared/utils/sla-modal-layout.utils';
import type {
  ConflictingPartnerScopeItem,
  ConflictReason,
  MaintenancePartnershipValidity,
  MaintenanceRulesStatus,
  MaintenanceSlaContractRecord,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/types';
import { useMaintenanceSlaContract } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/hooks/useMaintenanceSlaContract';
import { useMaintenanceSlaScopeOptions } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/hooks/useMaintenanceSlaScopeOptions';
import { MaintenanceSlaContractForm } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/components/MaintenanceSlaContractForm/MaintenanceSlaContractForm';

const CONFLICT_REASON_LABELS: Record<ConflictReason, string> = {
  LITERAL_CLAIMED_BY_OTHER: 'Assigned directly to the other partner',
  GEO_DOWN_COMMUNE_HAS_CLAIMED_ASSET:
    'Partner already owns an asset located in that commune',
  GEO_UP_ASSET_INSIDE_CLAIMED_COMMUNE:
    'Partner already owns the commune that asset is in',
  MANUFACTURER_DOWN_OWNED_MANUFACTURER_HAS_CLAIMED_ASSET:
    'Other partner owns that manufacturer',
  MANUFACTURER_UP_ASSET_OWNED_BY_OTHER_MANUFACTURER_CLAIMED:
    'Asset manufacturer already claimed by that partner',
  EXCLUDE_OVER_OWNED: 'Unit is owned already; exclusion meaningless here',
};

export interface MaintenanceSlaContractModalProps {
  open: boolean;
  organizationId: string;
  relationshipId: string;
  partnerName: string;
  partnership: MaintenancePartnershipValidity | null;
  contract: MaintenanceSlaContractRecord | null;
  onClose: () => void;
  onSave: (
    record: MaintenanceSlaContractRecord,
    rulesStatus: MaintenanceRulesStatus,
  ) => void | Promise<void>;
}

export function MaintenanceSlaContractModal({
  open,
  organizationId,
  relationshipId,
  partnerName,
  partnership,
  contract,
  onClose,
  onSave,
}: MaintenanceSlaContractModalProps) {
  const { t } = useTranslation('sla');
  const scopeOptions = useMaintenanceSlaScopeOptions(
    organizationId,
    relationshipId,
  );
  const state = useMaintenanceSlaContract({
    open,
    relationshipId,
    partnership,
    saved: contract,
    onSave,
    scopeAssetHierarchy: scopeOptions.options.assetHierarchy,
  });
  const modalLayout = resolveSlaModalLayout({
    partnerKind: 'oem',
    isEdit: true,
  });

  const dismiss = () => {
    state.cancel();
    onClose();
  };

  const handleSaveDraft = () => {
    state.clearConflicts();
    state.saveDraft();
  };

  const handlePublish = () => {
    state.clearConflicts();
    state.publish();
  };

  const conflictsContent = useMemo(() => {
    if (state.conflictingScopeOverlaps.length === 0) {
      return null;
    }

    return (
      <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
        {state.conflictingScopeOverlaps.map(
          (conflict: ConflictingPartnerScopeItem, index: number) => (
            <li
              key={`${conflict.claimedByRelationshipId}-${conflict.dimension}-${conflict.value}-${index}`}
            >
              <strong>[{conflict.claimedByPartnerName}]</strong>: dimension{' '}
              {conflict.dimension} &quot;{conflict.value}&quot; (
              {CONFLICT_REASON_LABELS[conflict.conflictReason]})
            </li>
          ),
        )}
      </ul>
    );
  }, [state.conflictingScopeOverlaps]);

  return (
    <SlaModalShell
      open={open}
      width={modalLayout.width}
      onClose={dismiss}
      title={
        <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography.Text
            strong
            style={{ fontSize: 16, color: '#191c1f', lineHeight: 1.3 }}
          >
            {t('oems.maintenance.formTitlePrefix')}
          </Typography.Text>
          <Typography.Text style={{ fontSize: 13, color: '#64748b' }}>
            {partnerName}
          </Typography.Text>
        </span>
      }
      footer={
        <Flex justify="space-between" align="center" style={{ width: '100%' }}>
          <Button onClick={dismiss}>{t('actions.cancel')}</Button>
          <Flex gap={8}>
            <Button loading={state.saving} onClick={handleSaveDraft}>
              {t('oems.maintenance.saveDraft')}
            </Button>
            <Button
              type="primary"
              loading={state.saving}
              disabled={state.issues.length > 0}
              onClick={handlePublish}
            >
              {t('oems.maintenance.publish')}
            </Button>
          </Flex>
        </Flex>
      }
      pinFooter={modalLayout.pinFooter}
      shellMaxHeight={modalLayout.shellMaxHeight}
      scrollBody={modalLayout.scrollBody}
      maxBodyHeight={modalLayout.maxBodyHeight}
    >
      {state.conflictingScopeOverlaps.length > 0 ? (
        <Alert
          type="error"
          showIcon
          closable
          onClose={state.clearConflicts}
          style={{ marginBottom: 16 }}
          message={
            'Scope rules cannot be published — some units are already assigned to another partner. Conflicts:'
          }
          description={conflictsContent}
        />
      ) : null}
      {partnership ? (
        <MaintenanceSlaContractForm
          partnership={partnership}
          state={state}
          scopeOptions={scopeOptions.options}
          scopeOptionsLoading={scopeOptions.isLoading}
        />
      ) : null}
    </SlaModalShell>
  );
}
